import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { configureHttpClient } from '../api/httpClient';
import { fetchAccessToken, login as loginRequest, logout as logoutRequest, refreshAccessToken } from '../api/authApi';
import { getUserFromToken, isAccessTokenExpired } from '../utils/jwt';

const AuthContext = createContext(null);
const ACCESS_TOKEN_STORAGE_KEY = 'localas.accessToken';

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const accessTokenRef = useRef(null);

  const applyAccessToken = useCallback((token) => {
    accessTokenRef.current = token;
    setAccessTokenState(token);
    setUser(token ? getUserFromToken(token) : null);

    if (token) {
      sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }
  }, []);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    setAccessTokenState(null);
    setUser(null);
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }, []);

  useEffect(() => {
    configureHttpClient({
      getAccessToken: () => accessTokenRef.current,
      setAccessToken: applyAccessToken,
      clearSession,
    });
  }, [applyAccessToken, clearSession]);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        if (storedToken && !isAccessTokenExpired(storedToken)) {
          applyAccessToken(storedToken);
        }

        let token = await fetchAccessToken();
        if (!token) {
          token = await refreshAccessToken();
        }

        if (token) {
          applyAccessToken(token);
          return;
        }

        if (!accessTokenRef.current) {
          clearSession();
        }
      } catch {
        if (!accessTokenRef.current) {
          clearSession();
        }
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, [applyAccessToken, clearSession]);

  const login = useCallback(async (username, password) => {
    const token = await loginRequest(username, password);
    applyAccessToken(token);
    return getUserFromToken(token);
  }, [applyAccessToken]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const token = await refreshAccessToken();
    if (token) {
      applyAccessToken(token);
    }
    return token;
  }, [applyAccessToken]);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      loading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refreshUser,
    }),
    [user, accessToken, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 안에서 사용해야 합니다.');
  }
  return context;
};
